<template>
  <div class="program-messages-step" data-test-id="program-messages-step">
    <div class="step-header">
      <Typography variant="h-5">Add a program message (optional)</Typography>
      <Typography v-if="addedDefaultMessages" variant="body-2">
        We've added a welcome message and a late-stage reminder to get you started. Feel free to
        edit, delete, or add more.
      </Typography>
    </div>

    <v-divider />

    <div class="messages-content">
      <div class="header-block">
        <Typography variant="subtitle-1">Program Messages</Typography>

        <div class="action-row">
          <Button text iconLeft="mdi-plus" @click="openNewMessage">Add Message</Button>
        </div>
      </div>

      <div v-if="messages.length === 0" class="empty-state-wrapper">
        <Typography variant="body-1" class="empty-state">No messages added yet.</Typography>
      </div>
      <div v-else class="message-list">
        <v-card
          v-for="msg in messages"
          :key="msg.id"
          outlined
          class="message-row"
          data-test-id="program-message-row"
          @click="openEditMessage(msg)"
        >
          <Typography variant="subtitle-2" class="message-row__title">
            {{ msg.messageSubject || "Incomplete message" }}
          </Typography>
          <div class="message-row__detail">
            <Typography variant="body-2" :class="isMessagePastProgramEnd(msg) && 'error--text'">
              {{ formatMessageDetail(msg) }}
            </Typography>
            <Icon
              v-if="isMessagePastProgramEnd(msg)"
              icon="mdi-alert-circle-outline"
              small
              color="error"
              aria-hidden="true"
              data-test-id="message-row-error"
            />
          </div>
          <div class="message-row__actions" @click.stop>
            <Button icon @click="openEditMessage(msg)" data-test-id="edit-message">
              <Icon icon="mdi-pencil" color="darkGrey" />
            </Button>
            <Button icon @click="handleDelete(msg)" data-test-id="delete-message">
              <Icon icon="mdi-delete" color="darkGrey" />
            </Button>
          </div>
        </v-card>
      </div>
    </div>

    <AddMessageSideSheet
      v-model="showAddMessage"
      :message="editingMessage"
      :program-length="programLength"
      @submit="handleSubmit"
    />

    <ErrorDialogV2
      v-model="showDeleteConfirm"
      title="Delete this message?"
      :message="deleteConfirmMessage"
      confirmButtonText="Delete"
      cancelButtonText="Cancel"
      :show-cancel-button="true"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </div>
</template>

<script>
import moment from "moment";
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import Icon from "@/components/generic/Icon.vue";
import ErrorDialogV2 from "@/components/ErrorDialogV2.vue";
import AddMessageSideSheet from "./AddMessageSideSheet.vue";

export default {
  name: "ProgramMessagesStep",
  components: {
    Typography,
    Button,
    Icon,
    ErrorDialogV2,
    AddMessageSideSheet,
  },
  props: {
    messages: {
      type: Array,
      default: () => [],
    },
    // Passed to the side sheet for scheduleValue upper-bound validation.
    programLength: {
      type: Number,
      default: null,
    },
    // Used to project each message's schedule onto a concrete send date for the
    // list row's detail column. When either date is unset we fall back to a
    // relative description ("3 days after program start").
    programStartDate: {
      type: String,
      default: null,
    },
    programEndDate: {
      type: String,
      default: null,
    },
    // Session flag driven by the parent. True when the parent has already
    // injected DEFAULT_MESSAGES into MessageDefinitions; gates the
    // "we've added..." subtitle at the top of this step.
    addedDefaultMessages: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:messages", "sheet-closed"],
  data() {
    return {
      showAddMessage: false,
      // Null → open sheet in Add mode. Object → open sheet pre-populated for edit.
      editingMessage: null,
      showDeleteConfirm: false,
      // Message queued for delete once the user confirms the dialog.
      pendingDelete: null,
    };
  },
  computed: {
    deleteConfirmMessage() {
      const subject = this.pendingDelete?.messageSubject;
      return subject
        ? `"${subject}" will be permanently removed from this program.`
        : "This message will be permanently removed from this program.";
    },
  },
  methods: {
    openNewMessage() {
      this.editingMessage = null;
      this.showAddMessage = true;
    },
    openEditMessage(msg) {
      // Clone so the sheet's local edits don't mutate the row until save.
      this.editingMessage = { ...msg };
      this.showAddMessage = true;
    },
    openMessageById(id) {
      const msg = this.messages.find((m) => m.id === id);
      if (msg) this.openEditMessage(msg);
    },
    handleSubmit(payload) {
      const next = [...this.messages];
      if (payload.id) {
        const idx = next.findIndex((m) => m.id === payload.id);
        next[idx] = { ...next[idx], ...payload };
      } else {
        // Client-side key only; newMessage tells PATCH to create, not update.
        next.push({ ...payload, id: Date.now(), newMessage: true });
      }
      this.$emit("update:messages", next);
    },
    handleDelete(msg) {
      this.pendingDelete = msg;
      this.showDeleteConfirm = true;
    },
    confirmDelete() {
      const target = this.pendingDelete;
      if (!target) return;
      this.$emit(
        "update:messages",
        this.messages.filter((m) => m.id !== target.id)
      );
      this.pendingDelete = null;
    },
    cancelDelete() {
      this.pendingDelete = null;
    },
    // Row-level "past program end" flag. Mirrors the threshold used by the
    // parent's `hasMessagePastProgramEnd` computed (and by AddMessageSideSheet's
    // scheduleValueRules) so this row's red detail lines up with the sidebar
    // step's red icon + label.
    isMessagePastProgramEnd(msg) {
      if (this.programLength == null) return false;
      const days = msg?.numDaysAfterProgramStart ?? msg?.numDaysToProgramEnd;
      return days != null && Number(days) > this.programLength;
    },
    // Return the concrete calendar date the message will fire on, when both
    // the message's schedule and the relevant program anchor date are set.
    // Falls back to a relative description so the row still communicates the
    // schedule when the user hasn't finished the Timeline step yet.
    formatMessageDetail(msg) {
      const isBeforeEnd = msg.numDaysToProgramEnd != null;
      const value = isBeforeEnd ? msg.numDaysToProgramEnd : msg.numDaysAfterProgramStart;
      if (value == null) return "";

      const anchor = isBeforeEnd ? this.programEndDate : this.programStartDate;
      if (anchor && moment(anchor).isValid()) {
        const date = isBeforeEnd
          ? moment(anchor).subtract(value, "days")
          : moment(anchor).add(value, "days");
        return date.format("MM/DD/YY");
      }

      const suffix = isBeforeEnd ? "before program end" : "after program start";
      const dayLabel = value === 1 ? "day" : "days";
      return `${value} ${dayLabel} ${suffix}`;
    },
  },
  watch: {
    showAddMessage(next, prev) {
      if (prev && !next) this.$emit("sheet-closed");
    },
  },
};
</script>

<style scoped>
.program-messages-step {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.step-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.messages-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.header-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.action-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.empty-state {
  text-align: center;
  padding-top: 8px;
}

.message-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.message-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 16px;
  cursor: pointer;
  border-width: 2px;
}

.message-row__title {
  width: 195px;
  flex: 0 0 195px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message-row__detail {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.message-row__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
}
</style>
